'use client';

/**
 * Public form input component that combines:
 * - `FieldWrapper` → label, required marker, tooltip, helper/error text.
 * - `BaseInput`    → value management, formatting, and validation logic.
 *
 * Usage patterns (high level):
 * - `onChange` is `(value, inputKey, event)` — the VALUE comes first. Reading the second
 *   argument as the value stores `inputKey` (usually undefined) and the field never accepts
 *   typing (PB-8222).
 * - Controlled:
 *   - `<Input value={value} onChange={(next) => setValue(next)} />`
 * - Uncontrolled:
 *   - `<Input defaultValue="foo" onChange={(next) => console.log(next)} />`
 * - With formatter:
 *   - `<Input formatter={(value, prev) => formatPhone(value, prev)} />`
 * - With validators:
 *   - `validators={[new InputValidator('Message', v => isValid(v))]} required`
 *
 * The `error` prop can override internal validation:
 * - `error="Message"` → show this message and mark field invalid.
 * - `error={true}`    → mark field invalid with no specific message.
 * - `error={false}`   → rely solely on required + validators.
 */

import { forwardRef, useCallback, useId, useState } from 'react';
import { BaseInput } from './BaseInput';
import { FieldWrapper } from './FieldWrapper';
import type { BaseInputProps, InputTypeFormatter } from './BaseInput';

export type { InputTypeFormatter };

/** Props for the high-level `Input` component (re-export of `BaseInputProps`). */
export type InputProps = BaseInputProps;

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(props, ref) {
  // Ensure each field instance has a stable ID for label and helper text wiring.
  const generatedId = useId();
  const id = props.id ?? generatedId;
  const helperId = `${id}-helper`;

  // Internal error coming from `BaseInput` validation (required + validators).
  const [validationError, setValidationError] = useState<string | null>(null);
  const handleValidationChange = useCallback((error: string | null) => {
    setValidationError(error);
  }, []);

  // External error from the parent takes precedence over internal validation.
  const { error: controlledError } = props;
  const displayError =
    typeof controlledError === 'string'
      ? controlledError
      : controlledError === true
        ? ''
        : validationError;

  return (
    <FieldWrapper
      label={props.label}
      required={props.required}
      requiredMarker={props.requiredMarker}
      infoTooltip={props.infoTooltip}
      error={displayError}
      helperText={props.helperText}
      id={id}
      helperId={helperId}
      className={props.className ?? ''}
      hideBottomPadding={props.hideBottomPadding}
    >
      {/* Forward all props to `BaseInput`, adding the callback that reports validation changes. */}
      <BaseInput ref={ref} {...props} id={id} onValidationChange={handleValidationChange} />
    </FieldWrapper>
  );
});
